import { IGetSalonEmployeesByOwnerIdAction } from "@/actions/owner/types";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { FaRegEdit } from "react-icons/fa";

export const OwnerCompanyEmployeesTableColumns: (
  toggleActiveFn: (employeeId: string, isActive: boolean) => void,
  openEditModalFn: (
    employee: IGetSalonEmployeesByOwnerIdAction["Response"][number],
  ) => void,
) => ColumnDef<IGetSalonEmployeesByOwnerIdAction["Response"][number]>[] = (
  toggleActiveFn,
  openEditModalFn,
) => {
  return [
    {
      accessorKey: "name",
      header: "Nome",
      cell: ({ row }) => row.getValue("name"),
    },
    {
      accessorKey: "phone",
      header: "Número",
      cell: ({ row }) => row.getValue("phone"),
    },
    {
      accessorKey: "vocation",
      header: "Serviço",
      filterFn: (row, columnId, filterValue) => {
        const searchValue = (filterValue ?? "").toLowerCase();
        const vocations = row.original.vocations || [];

        return vocations.some((vocation) =>
          vocation.name.toLowerCase().includes(searchValue),
        );
      },
      cell: ({ row }) => {
        const vocations = row.original.vocations || [];

        const hiddenVocations = vocations.slice(2);

        if (vocations.length === 0) {
          return <span className="text-muted-foreground">Nenhum serviço</span>;
        }

        return (
          <div className="flex flex-wrap gap-1 items-center">
            {vocations.slice(0, 2).map((vocation, index) => (
              <Badge
                key={index}
                variant="outline"
                className="bg-[#4b5563] text-content-primary"
              >
                {vocation.name}
              </Badge>
            ))}

            {hiddenVocations.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className="cursor-pointer border-none p-0"
                    >
                      ...
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs break-words">
                    {hiddenVocations.map((v, i) => (
                      <span key={i} className="block">
                        {v.name}
                      </span>
                    ))}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => <div className="text-center w-full">Status</div>,
      cell: ({ row }) => {
        const isActive = row.original.active;

        return (
          <div className="flex justify-center items-center">
            <span className="text-sm mr-2">
              {isActive ? "Ativo" : "Inativo"}
            </span>
            <Switch
              className={isActive ? "!bg-[#15803D]" : ""}
              checked={isActive}
              onCheckedChange={(checked) =>
                toggleActiveFn(row.original.id, checked)
              }
              aria-label={
                isActive ? "Desativar funcionário" : "Ativar funcionário"
              }
            />
          </div>
        );
      },
    },
    {
      id: "editProfession",
      header: () => <div className="text-center w-full">Ação</div>,
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-center w-full">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEditModalFn(row.original)}
              aria-label="Editar profissão"
              className="hover:bg-transparent min-w-[70px]"
            >
              <FaRegEdit className="h-4 w-4" />
              Editar
            </Button>
          </div>
        );
      },
    },
  ];
};
